import React, { useState, useEffect, useCallback, useMemo } from "react";
import DataTable from "react-data-table-component";
import { Tooltip, OverlayTrigger, Button } from "react-bootstrap";
import DateTime from "../../../../components/Formatters/DateTime";
import TextSpinner from "../../../../components/UI/Spinner/TextSpinner";
import useDetalhamento from "../hooks/useDetalhamento";
import FormDemonstrativos from "../FormDemonstrativos";
import HtmlToString from "../../../../components/Formatters/HtmlToString";
import useMontarLogica from "../hooks/useMontarLogica";
import useExcluirDemonstrativo from "../hooks/useExcluirDemonstrativo";
import constants from "../../../Constants";

const getColumns = () =>
  [
    {
      name: "Descrição",
      selector: (row) => <HtmlToString html={row.dsItem} />,
      maxWidth: "20%",
    },
    {
      name: "Referência",
      selector: (row) => row.txReferencia,
      maxWidth: "10%",
      center: true,
    },
    {
      name: "Data de Vencimento",
      selector: (row) => row.dtVencimento,
      maxWidth: "10%",
      cell: (row) => <DateTime value={row.dtVencimento} stringFormat="dd/MM/yyyy" />,
      center: true,
    },

    {
      name: "ICMS Declarado",
      selector: (row) =>
        row.vlDeclarado
          ? row.vlDeclarado.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
          : row.vlDeclarado,
      maxWidth: "10%",
      center: true,
    },
    {
      name: "ICMS Apurado",
      selector: (row) =>
        row.vlApurado ? row.vlApurado.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : row.vlApurado,
      maxWidth: "10%",
      center: true,
    },
    {
      name: "ICMS Recolhido",
      selector: (row) =>
        row.vlIcmsRecolhido
          ? row.vlIcmsRecolhido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
          : row.vlIcmsRecolhido,
      maxWidth: "10%",
      center: true,
    },

    {
      name: "ICMS a Recolher",
      selector: (row) => row.vlIcms.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
      maxWidth: "10%",
      center: true,
    },
    {
      name: "Multa",
      selector: (row) => row.vlMultaOcorrenciaAtual.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
      maxWidth: "10%",
      center: true,
    },
    {
      name: "Total",
      selector: (row) =>
        (row.vlMultaOcorrenciaAtual + row.vlIcms).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
      maxWidth: "10%",
      center: true,
    },
  ].filter(Boolean);

const conditionalRowStyles = [
  {
    when: (row) => row.dsItem === "Total",
    style: {
      backgroundColor: "#f0f0f0",
      cursor: "not-allowed",
      fontWeight: "bold", // Deixa a fonte em negrito
      color: "#333", // (opcional) ajusta a cor da fonte pra ficar mais visível
    },
  },
];

const ListagemJ1 = ({ history, sqItemOcorrencia, sqAcaoFiscal }) => {
  const [reload, setReload] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const { dataWithTotal, totalRows, isLoading, handlePageChange } = useDetalhamento({
    sqAcaoFiscal,
    sqItemOcorrencia,
    reload, // Passando para o hook para atualizar os dados
  });

  const [dados, setDados] = useState({});

  const { montarLogica, mostrarLista, setMostrarLista } = useMontarLogica();

  useEffect(() => {
    if (mostrarLista) {
      montarLogica(1);
    }
  }, [montarLogica, mostrarLista, reload]);

  const voltarListagem = () => {
    setMostrarLista(true);
    setReload((prev) => prev + 1);
  };

  const { ModalExcluirDemonstrativo, setShowExcluirConfirm } = useExcluirDemonstrativo({
    setReload,
    setItensSelecionados,
  });

  const columns = useMemo(() => getColumns(), []);

  const preparaDelete = useCallback(() => {
    if (itensSelecionados.length > 0) {
      setShowExcluirConfirm({
        show: true,
        body: `Confirma exclusão do(s) item(ns) no demonstrativo?`,
        context: { sqAcaoFiscal, sqItemOcorrencia, listaItens: itensSelecionados },
      });

      // Resetando a seleção e forçando novo fetch dos dados
      setClearSelectedRows(!clearSelectedRows);
      setItensSelecionados([]);

      // Força o reload completo para pegar os dados atualizados
      setReload((prev) => !prev);
    } else {
      document.getElementById("alerta").innerHTML = "Selecione pelo menos 1 item para excluir";
    }
  }, [itensSelecionados, sqAcaoFiscal, sqItemOcorrencia, setShowExcluirConfirm, clearSelectedRows]);

  return mostrarLista ? (
    <>
      {ModalExcluirDemonstrativo}
      <DataTable
        noHeader
        keyField={(row, index) => `${row.sqItemOcorrencia}-${index}`}
        columns={columns}
        data={dataWithTotal}
        progressPending={isLoading}
        noDataComponent="Nenhum registro para exibir"
        progressComponent={<TextSpinner />}
        pointerOnHover
        onRowClicked={(row) => {
          if (row.dsItem !== "Total") {
            setDados(row);
            montarLogica(4);
          }
        }}
        conditionalRowStyles={conditionalRowStyles}
        pagination
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        paginationComponentOptions={constants.paginationComponentOptions}
        paginationPerPage={10}
        selectableRows
        selectableRowDisabled={(row) => row.dsItem === "Total"}
        selectableRowsVisibleOnly={(row) => !(row.dsItem === "Total")}
        onSelectedRowsChange={(state) => {
          setItensSelecionados(state.selectedRows);
        }}
        clearSelectedRows={clearSelectedRows}
      />

      <span className="alerta" hidden={itensSelecionados.length > 0} id="alerta">
        &nbsp;
      </span>

      <br />
      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-chave">Excluir itens do demonstrativo</Tooltip>}>
        <Button
          className="btn btn-primary btn-md mt-1" // btn btn-primary btn-md mr-2 btn btn-primary
          style={{ marginTop: "3px" }} // CSS inline direto
          disabled={itensSelecionados.length === 0}
          onClick={() => preparaDelete()}
        >
          Excluir
        </Button>
      </OverlayTrigger>
    </>
  ) : (
    <FormDemonstrativos
      tipoDetalhamento={dados.tipoDetalhamento}
      sqItemOcorrencia={dados.sqItemOcorrencia}
      sqAcaoFiscal={dados.sqAcaoFiscal}
      dadosOcorrencia={dados}
      history={history}
      voltarListagem={() => voltarListagem()}
    />
  );
};

export default ListagemJ1;
